import React, { useRef, useState } from "react";
import ErrorPopup from "./ErrorPopup";
import Loader from "./Loader";
import { produrl, textMapping } from "../utils/helper";

const PRESIGNED_URL_ENDPOINT =
  produrl + "api/v1/photo/generate_upload_url";
const COMMIT_UPLOAD_ENDPOINT = produrl + "api/v1/pm/upload/photo";

const errorStyle = {
  color: "red",
};

const ImageUploadPopup = ({ ImagePopupClose, UploadHanlde }) => {
  const [imageURL, setImageURL] = useState(null);
  const [showImage, setShowImage] = useState(false);
  const [errorpopup, setErrorpopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errormsg, setErrormsg] = useState("");
  const [message, setMessage] = useState("");
  const fileInputRef = useRef(null);
  const allowFiletype = ["image/heic", "image/png", "image/jpeg", "image/jpg"];

  const requestPresignedUrl = async (fileName, contentType) => {
    const token = localStorage.getItem("acesss");
    if (!token) {
      throw new Error("Not authenticated. Please reload the app and try again.");
    }

    const response = await fetch(PRESIGNED_URL_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({
        file_name: fileName,
        content_type: contentType,
        image_type: "profile",
      }),
    });

    if (response.status === 401) {
      throw new Error("Session expired. Please reload the app and try again.");
    }
    if (!response.ok) {
      throw new Error(
        "Failed to obtain upload URL (status " + response.status + ")"
      );
    }
    return response.json();
  };

  const putImageToSignedUrl = async (signedUrl, fileBlob, contentType) => {
    const response = await fetch(signedUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: fileBlob,
    });

    if (!response.ok) {
      throw new Error("S3 upload failed (status " + response.status + ")");
    }
    return response;
  };

  const commitUploadedPhoto = async (imagePath) => {
    const token = localStorage.getItem("acesss");
    if (!token) {
      throw new Error("Not authenticated. Please reload the app and try again.");
    }

    const response = await fetch(COMMIT_UPLOAD_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ image_path: imagePath }),
    });

    if (response.status === 401) {
      throw new Error("Session expired. Please reload the app and try again.");
    }
    if (!response.ok) {
      throw new Error(
        "Failed to register uploaded photo (status " + response.status + ")"
      );
    }
    return response.json().catch(() => ({}));
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadHandler = async (e) => {
    setLoading(true);
    const file = e.target.files[0];
    if (!file) {
      setLoading(false);
      return;
    }
    if (!allowFiletype.includes(file.type)) {
      setErrormsg("Please select a valid image file (JPEG, PNG, HEIC).");
      setLoading(false);
      resetFileInput();
      return;
    }

    setErrormsg("");
    const sessionid = Date.now();
    const random = Math.floor(100000 + Math.random() * 900000);

    try {
      // Compress first so the signed Content-Type matches what S3 actually receives.
      // canvas.toBlob may fall back to image/png for inputs the browser can't decode (e.g. HEIC).
      const compressedFile = await compressImage(file);
      const effectiveType = compressedFile.type || file.type;
      const ext = (effectiveType.split("/")[1] || "jpg").replace("jpeg", "jpg");
      const fileName = `${random}_${sessionid}.${ext}`;

      const responseData = await requestPresignedUrl(fileName, effectiveType);
      const payload = responseData.data || responseData;
      const uploadUrl = payload.url;
      const imageKey = payload.key;

      if (!uploadUrl) {
        throw new Error("Upload URL missing in server response");
      }
      if (!imageKey) {
        throw new Error("Image key missing in server response");
      }

      await putImageToSignedUrl(uploadUrl, compressedFile, effectiveType);

      const commitResponse = await commitUploadedPhoto(imageKey);
      const commitPayload =
        (commitResponse && commitResponse.data) || commitResponse || {};
      const fileUrl =
        commitPayload.url ||
        commitPayload.photo_url ||
        commitPayload.file_url ||
        uploadUrl.split("?")[0];

      setLoading(false);
      setImageURL(fileUrl);
      setShowImage(true);
    } catch (error) {
      setErrorpopup(true);
      setMessage(error && error.message ? error.message : String(error));
      setLoading(false);
      resetFileInput();
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const scaleFactor = 0.7;
          canvas.width = Math.max(1, Math.round(img.width * scaleFactor));
          canvas.height = Math.max(1, Math.round(img.height * scaleFactor));
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Image compression failed"));
                return;
              }
              resolve(new File([blob], file.name, { type: blob.type }));
            },
            file.type,
            0.7
          );
        };
        img.onerror = () =>
          reject(new Error("Unable to read image. Try a JPEG or PNG."));
        img.src = event.target.result;
      };
      reader.onerror = () => reject(new Error("Failed to read selected file"));
    });
  };

  const popupclose = () => {
    setErrorpopup(false);
  };

  return (
    <>
      {loading && <Loader />}
      <div className="popup_bg">
        <div className="imgetype_bg">
          <div className="selcttype_img">
            {!showImage && (
              <div>
                <img
                  src="/assets/images/cloud.png"
                  className="upload_icons"
                  alt="Upload Icon"
                />
                <h4>{textMapping("Select")}</h4>

                <ul className="uploadtype_list">
                  <li>
                    <div className="gallery_uplpad">
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept=".png, .jpeg, .jpg, .heic"
                        onChange={uploadHandler}
                      />
                      <h6>{textMapping("Gallery")}</h6>
                    </div>
                  </li>
                </ul>
                <p>Support format: JPEG, JPG, PNG, HEIC</p>
              </div>
            )}

            {showImage && (
              <div>
                <img
                  src={imageURL ? imageURL : "/assets/images/userdefault.jpg"}
                  className="upload_img_preview"
                  alt=""
                />
                <button className="cancel_btn" onClick={ImagePopupClose}>
                  {textMapping("Cancel")}
                </button>
              </div>
            )}
            {errormsg && <p style={errorStyle}>{errormsg} </p>}
          </div>

          {!showImage && (
            <button className="cancel_btn" onClick={ImagePopupClose}>
              {textMapping("Cancel")}
            </button>
          )}

          {showImage && (
            <button
              className="uploadimg_btn"
              onClick={() => UploadHanlde(imageURL)}
            >
              {textMapping("Upload")}
            </button>
          )}
        </div>
      </div>
      {errorpopup && <ErrorPopup message={message} popupclose={popupclose} />}
    </>
  );
};

export default ImageUploadPopup;
